import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../services/authService";
import api from "../../services/api";
import { exitInstitute } from "./instituteContextSlice";
import { fetchMyPermissions } from "./permissionsSlice";
import { clearPermissions } from "./permissionsSlice";
import { recordLastLogoutCode } from "../../utils/sessionRevocation";

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  message: null,
  consentRequired: false,
  activeConsentForm: null,
  consentMessage: null,
  isFirstLogin: false,
};

// Async Thunks
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed"
      );
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    // First-wins reason, recorded before the API call so the SESSION_REVOKED echo
    // (or a 401 on the logout POST) can never relabel a user-initiated logout.
    recordLastLogoutCode("USER_LOGOUT");
    try {
      await authService.logout();
      dispatch(exitInstitute());
      dispatch(clearPermissions());
      return null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send reset email"
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp(email, otp);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "OTP verification failed"
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (resetData, { rejectWithValue }) => {
    try {
      // resetData can be { token, password } or { email, password }
      const response = await authService.resetPassword(resetData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password reset failed"
      );
    }
  }
);



async function resolveConsentStatusForUser(user) {
  let consentRequired = user?.consentFlag === 0;
  let activeConsentForm = null;

  const token = localStorage.getItem("accessToken");
  if (!token || !user) {
    return { consentRequired, activeConsentForm };
  }

  // Backend requireConsent middleware already skips Super Admins; mirror that on the client
  // so Manage Institute / refresh does not re-prompt after consent was accepted.
  const role = String(user.role || "").toLowerCase().trim();
  const isManagingInstitute =
    Boolean(user.isManagingInstitute) || Boolean(localStorage.getItem("originalSuperAdminId"));
  if (role === "superadmin" || role === "super_admin" || isManagingInstitute) {
    return { consentRequired: false, activeConsentForm: null };
  }

  try {
    const consentRes = await api.get("/web/auth/consent-forms");
    const forms = Array.isArray(consentRes.data?.data) ? consentRes.data.data : [];
    const requiredForm = forms.find((f) => f.isRequired) || forms[0];
    if (requiredForm) {
      const accepted =
        user.consentFlag !== 0 &&
        user.acceptedPrivacyPolicyId &&
        user.acceptedPrivacyPolicyId === requiredForm.uuid;
      if (!accepted) {
        consentRequired = true;
        activeConsentForm = { uuid: requiredForm.uuid, title: requiredForm.title };
      } else {
        consentRequired = false;
      }
    }
  } catch {
    /* consent-forms may be unavailable; interceptor handles mid-session updates */
  }

  return { consentRequired, activeConsentForm };
}

export const checkAuth = createAsyncThunk(
  "auth/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.checkAuth();
      const user = response.data?.user;
      if (!user) return rejectWithValue(null);
      const consent = await resolveConsentStatusForUser(user);
      return { user, ...consent };
    } catch (error) {
      return rejectWithValue(null);
    }
  }
);

export const acceptConsent = createAsyncThunk(
  "auth/acceptConsent",
  async (consentFormId, { rejectWithValue }) => {
    try {
      const response = await authService.acceptConsent(consentFormId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to accept consent"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setConsentRequired: (state, action) => {
      state.consentRequired = action.payload;
    },
    setConsentGate: (state, action) => {
      const { consentRequired, activeConsentForm, message } = action.payload || {};
      if (consentRequired !== undefined) state.consentRequired = Boolean(consentRequired);
      if (activeConsentForm !== undefined) state.activeConsentForm = activeConsentForm;
      if (message) state.consentMessage = message;
    },
    clearConsentMessage: (state) => {
      state.consentMessage = null;
    },
    // Update user in state (e.g. after profile update) so Redux and consumers have latest data
    setUser: (state, action) => {
      if (!action.payload) return;
      state.user = state.user ? { ...state.user, ...action.payload } : action.payload;
      if (action.payload.firstLogin === false) state.isFirstLogin = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login - don't set global loading; Login shows loader on button
      // Login form has its own button loading state
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const user = action.payload?.user;
        if (user) {
          state.isAuthenticated = true;
          state.user = user;
          // Clear institute context for super admin on login
          if (user.role === "superadmin") {
            localStorage.removeItem("selectedInstitute");
            localStorage.removeItem("isInInstituteContext");
            localStorage.removeItem("originalSuperAdminId");
          }
          state.consentRequired = action.payload?.consentRequired || user.consentFlag === 0 || false;
          state.activeConsentForm = action.payload?.activeConsentForm || null;
          if (action.payload?.consentRequired) {
            state.consentMessage =
              action.payload?.message ||
              "Please accept the updated privacy policy to continue. You remain signed in.";
          }
          state.isFirstLogin = action.payload?.isFirstLogin || false;
          // Store message from backend if available
          state.message = action.payload?.message || action.payload?.data?.message || null;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.consentRequired = false;
          state.isFirstLogin = false;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.consentRequired = false;
        state.isFirstLogin = false;
        // Clear institute context on logout
        localStorage.removeItem("selectedInstitute");
        localStorage.removeItem("isInInstituteContext");
        localStorage.removeItem("originalSuperAdminId");
      })
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.consentRequired = Boolean(action.payload.consentRequired);
        state.activeConsentForm = action.payload.activeConsentForm || null;
        if (action.payload.consentRequired && !state.consentMessage) {
          state.consentMessage =
            "A new privacy policy requires your acceptance. You are still signed in — please review and accept below to continue.";
        }
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Accept Consent
      .addCase(acceptConsent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptConsent.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.consentRequired = false;
        state.activeConsentForm = null;
        state.consentMessage = null;
        // Update user's consent flag to indicate consent has been accepted
        if (state.user) {
          state.user.consentFlag = 1;
          // Store the accepted consent form ID so we don't show this form again
          // acceptedPrivacyPolicyId will be passed from the action
          if (action.meta?.arg) {
            state.user.acceptedPrivacyPolicyId = action.meta.arg;
          }
          try {
            localStorage.setItem("user", JSON.stringify(state.user));
          } catch {
            /* ignore storage errors */
          }
        }
      })
      .addCase(acceptConsent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload?.message || "OTP sent to your email";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload?.message || "OTP verified successfully";
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload?.message || "Password reset successfully";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setConsentRequired, setConsentGate, clearConsentMessage, setUser } =
  authSlice.actions;
export default authSlice.reducer;
